import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TextField, Typography } from "@mui/material";
import userApi from "../../api/users";

interface Transaction {
  _id: string; type: string; source: string; amount: number; status: string; date: string;
  reference?: string; displayTitle?: string; reason?: string; adminId?: string; adminName?: string;
}
interface Wallet {
  balance: number; pendingBonus: number; transactions: Transaction[]; canCredit: boolean;
}
const money = (value: number) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(value);
const labels: Record<string, string> = { manual_admin: "Manual admin credit", signup_bonus: "Signup bonus",
  referral: "Referral", checkout: "Checkout", instant_refund: "Instant refund" };

export default function UserWallet({ userId, userName }: { userId: string; userName: string }) {
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [loadError, setLoadError] = useState("");
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [displayTitle, setDisplayTitle] = useState("Wallet Credit");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [page, setPage] = useState(0);
  const inFlight = useRef(false);
  // Preserve the exact payload after an uncertain response so a retry cannot double-credit.
  const submission = useRef<{ amount: number; reason: string; requestId: string; displayTitle: string } | null>(null);
  const [locked, setLocked] = useState(false);
  const load = async () => {
    setLoadError("");
    try { setWallet(await userApi.getWallet(userId)); }
    catch { setLoadError("Unable to load wallet. Please retry."); }
  };
  useEffect(() => { load(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps
  const numericAmount = Number(amount);
  const valid = /^\d+(\.\d{1,2})?$/.test(amount) && numericAmount > 0 &&
    Number.isSafeInteger(Math.round(numericAmount * 100)) && reason.trim().length > 0 && reason.trim().length <= 500;
  const credit = async () => {
    if (inFlight.current || !valid) return;
    inFlight.current = true;
    setSaving(true); setError("");
    try {
      if (!submission.current) submission.current = { amount: numericAmount, reason: reason.trim(), displayTitle: displayTitle.trim() || "Wallet Credit", requestId: crypto.randomUUID() };
      setLocked(true);
      const updated = await userApi.addWalletCredit(userId, submission.current);
      setWallet(updated); setPage(0); setOpen(false);
      setSuccess(`${money(submission.current.amount)} credited successfully.`);
      submission.current = null; setLocked(false); setAmount(""); setReason(""); setDisplayTitle("Wallet Credit");
    } catch (err: any) {
      setError(err.response?.data?.message || "Credit could not be confirmed. Retry this submission to safely confirm it.");
      // A validation/access rejection cannot have committed a credit.
      if ([400, 401, 403, 404].includes(err.response?.status)) {
        submission.current = null; setLocked(false);
      }
    } finally { inFlight.current = false; setSaving(false); }
  };
  return <Card sx={{ mt: 3 }}>
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h6">Wallet</Typography>
        {wallet?.canCredit && <Button variant="contained" onClick={() => { setSuccess(""); setOpen(true); }}>Add credits</Button>}
      </Stack>
      {loadError && <Alert severity="error" action={<Button onClick={load}>Retry</Button>}>{loadError}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {!wallet && !loadError && <Typography>Loading wallet…</Typography>}
      {wallet && <>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={4} sx={{ mb: 2 }}>
          <Box><Typography color="text.secondary">Available balance</Typography><Typography variant="h5">{money(wallet.balance)}</Typography></Box>
          <Box><Typography color="text.secondary">Pending referral / bonus credits</Typography><Typography variant="h5">{money(wallet.pendingBonus)}</Typography></Box>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Pending credits are unavailable until activated. Existing wallet spending limits apply.</Typography>
        <Typography variant="subtitle1">Transaction history</Typography>
        {!wallet.transactions.length ? <Typography color="text.secondary">No wallet transactions yet.</Typography> : <>
          <TableContainer><Table size="small" aria-label="Wallet transactions">
            <TableHead><TableRow>{["Date", "Type", "Amount", "Source", "Status", "Customer-facing title", "Reason / reference", "Added by"].map(label => <TableCell key={label}>{label}</TableCell>)}</TableRow></TableHead>
            <TableBody>{wallet.transactions.slice(page * 10, page * 10 + 10).map(t => <TableRow key={t._id}>
              <TableCell>{new Date(t.date).toLocaleString("en-IN")}</TableCell>
              <TableCell>{t.type === "credit" ? "Credit" : "Debit"}</TableCell>
              <TableCell>{t.type === "credit" ? "+" : "−"}{money(t.amount)}</TableCell>
              <TableCell>{labels[t.source] || t.source}</TableCell>
              <TableCell><Chip size="small" label={t.status === "active" ? "Active" : "Pending"} color={t.status === "active" ? "success" : "warning"} /></TableCell>
              <TableCell>{t.source === "manual_admin" ? t.displayTitle || "Wallet Credit" : "—"}</TableCell>
              <TableCell sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", maxWidth: 350 }}>{t.reason || t.reference || "—"}</TableCell>
              <TableCell>{t.adminName || t.adminId || "—"}</TableCell>
            </TableRow>)}</TableBody>
          </Table></TableContainer>
          <TablePagination component="div" count={wallet.transactions.length} page={page} rowsPerPage={10} rowsPerPageOptions={[10]} onPageChange={(_, next) => setPage(next)} />
        </>}
      </>}
    </CardContent>
    <Dialog open={open} onClose={() => { if (!saving) setOpen(false); }} fullWidth maxWidth="sm">
      <DialogTitle>Add wallet credits</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>Credit wallet for {userName}.</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField label="Amount (₹)" value={amount} onChange={e => setAmount(e.target.value)} disabled={locked} fullWidth margin="dense" inputProps={{ inputMode: "decimal" }} helperText="Enter a positive amount with up to two decimal places." />
        <TextField label="Customer-facing title" value={displayTitle} onChange={e => setDisplayTitle(e.target.value)} disabled={locked} fullWidth margin="dense" inputProps={{ maxLength: 80 }} helperText="Shown in the customer’s wallet, e.g. Goodwill Credit. Defaults to Wallet Credit." />
        <TextField label="Reason" value={reason} onChange={e => setReason(e.target.value)} disabled={locked} helperText="Internal admin note; not shown to the customer." required fullWidth margin="dense" multiline minRows={2} inputProps={{ maxLength: 500 }} />
        <Typography sx={{ mt: 2 }}>Current balance: {money(wallet?.balance || 0)}</Typography>
        {valid && <Typography>Balance after credit: {money((wallet?.balance || 0) + numericAmount)}</Typography>}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Credits become available immediately. Your admin identity and reason will be recorded.</Typography>
      </DialogContent>
      <DialogActions>
        <Button disabled={saving} onClick={() => setOpen(false)}>Close</Button>
        <Button variant="contained" disabled={!valid || saving} onClick={credit}>{saving ? "Adding…" : locked ? "Retry credit" : "Confirm credit"}</Button>
      </DialogActions>
    </Dialog>
  </Card>;
}
